import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { WizardStepTemplate } from "./wizard-step-template";
import { WizardStepBasicInfo } from "./wizard-step-basic-info";
import { WizardStepRewards } from "./wizard-step-rewards";
import { StepDot, type StepDotState } from "@/components/ui/step-dot";
import {
  createInitiative,
  buildCreateInitiativePayload,
  buildJuthoorInitiativePayload,
} from "@/lib/api/initiatives";
import { createJuthoorInitiative } from "@/lib/api/juthoor";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import type { Locale } from "@/types/auth";
import type {
  InitiativeTemplate,
  CreateInitiativeInput,
  CreateInitiativeRewardsInput,
} from "@/types/initiative";
import type { JuthoorRewardsInput } from "@/types/juthoor";

type WizardStep = 0 | 1 | 2;

interface CreateInitiativeWizardProps {
  open: boolean;
  locale: Locale;
  onClose: () => void;
  onSuccess: () => void;
}

interface StepDef {
  num: string;
  ar: string;
  en: string;
  helperAr: string;
  helperEn: string;
  tipsAr: string[];
  tipsEn: string[];
}

const STEPS: StepDef[] = [
  {
    num: "01",
    ar: "اختر النوع",
    en: "Pick a type",
    helperAr: "اختر نموذج المبادرة الذي يناسب أهدافك.",
    helperEn: "Choose the initiative template that fits your goal.",
    tipsAr: ["يمكن تعديل التفاصيل لاحقاً", "كلّ نموذج له مكافآت مختلفة"],
    tipsEn: ["DETAILS CAN BE ADJUSTED LATER", "EACH TEMPLATE HAS DIFFERENT REWARDS"],
  },
  {
    num: "02",
    ar: "المعلومات الأساسية",
    en: "Basic info",
    helperAr: "أضف العنوان، الوصف، والموقع.",
    helperEn: "Add the title, description, and location.",
    tipsAr: ["العنوان مرئي للمشاركين", "اختر مدينة دقيقة"],
    tipsEn: ["TITLE IS VISIBLE TO PARTICIPANTS", "PICK A SPECIFIC CITY"],
  },
  {
    num: "03",
    ar: "المكافآت والميزانية",
    en: "Rewards & budget",
    helperAr: "حدِّد المكافآت والميزانية المخصّصة.",
    helperEn: "Configure rewards and the allocated budget.",
    tipsAr: ["الميزانية تُحجز فوراً", "يمكن إضافة الممكِّنين بعد الإطلاق"],
    tipsEn: ["BUDGET RESERVES IMMEDIATELY", "ENABLERS CAN BE ADDED AFTER LAUNCH"],
  },
];

export function CreateInitiativeWizard({
  open,
  locale,
  onClose,
  onSuccess,
}: CreateInitiativeWizardProps) {
  const isRTL = locale === "ar";
  const [step, setStep] = useState<WizardStep>(0);
  const [selectedTemplate, setSelectedTemplate] =
    useState<InitiativeTemplate | null>(null);
  const [basicInfo, setBasicInfo] = useState<CreateInitiativeInput | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!open) return null;

  const handleTemplateSelect = (template: InitiativeTemplate) => {
    setSelectedTemplate(template);
  };

  const handleTemplateNext = () => {
    if (!selectedTemplate) return;
    setStep(1);
  };

  const handleBasicInfoSubmit = (data: CreateInitiativeInput) => {
    setBasicInfo({ ...data, template_id: selectedTemplate?.id || "" });
    setStep(2);
  };

  const handleRewardsSubmit = async (
    rewardsData: CreateInitiativeRewardsInput,
    initBudget: number,
  ) => {
    if (!basicInfo || !selectedTemplate) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = buildCreateInitiativePayload(
        selectedTemplate,
        basicInfo,
        rewardsData,
        initBudget,
      );
      const response = await createInitiative(payload);
      if (response.success) {
        onSuccess();
      } else {
        setSubmitError(response.message || t(locale, "error"));
      }
    } catch {
      setSubmitError(t(locale, "error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleJuthoorRewardsSubmit = async (juthoorData: JuthoorRewardsInput) => {
    if (!basicInfo) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = buildJuthoorInitiativePayload(basicInfo, juthoorData);
      const response = await createJuthoorInitiative(payload);
      if (response.success) {
        onSuccess();
      } else {
        setSubmitError(response.message || t(locale, "error"));
      }
    } catch {
      setSubmitError(t(locale, "error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setSelectedTemplate(null);
    setBasicInfo(null);
    setSubmitError(null);
    onClose();
  };

  const currentStepDef = STEPS[step]!;

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-title"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="bg-card w-full max-w-5xl rounded-3xl shadow-2xl border border-border overflow-hidden flex flex-col max-h-[92vh]">
        {/* ─── Header + progress rail ────────────────────────────── */}
        <div className="px-6 md:px-8 pt-6 md:pt-8 pb-5 border-b border-border space-y-5">
          <div className="flex items-start justify-between gap-4">
            <h1
              id="wizard-title"
              className="font-display font-bold text-2xl md:text-3xl text-foreground tracking-tight"
            >
              {t(locale, "newInitiative")}
            </h1>
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-xl border border-border bg-card hover:bg-muted transition-colors"
              aria-label={t(locale, "close")}
            >
              <X className="w-4 h-4 text-foreground-secondary" strokeWidth={2} />
            </button>
          </div>

          <ProgressRail step={step} isRTL={isRTL} />
        </div>

        {/* ─── Body ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {step === 2 ? (
            // Step 3 (Rewards & budget) — full-width single column.
            // Header pinned to the top so the form gets the full width below.
            <div className="px-6 md:px-8 py-6 md:py-8 space-y-6">
              <StepHeader stepDef={currentStepDef} isRTL={isRTL} />

              <div className="min-w-0">
                {submitting && (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <span className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                      <span className="font-sans text-[10px] tracking-[0.25em] text-foreground-secondary uppercase">
                        {t(locale, "loading")}
                      </span>
                    </div>
                  </div>
                )}
                {!submitting && (
                  <WizardStepRewards
                    locale={locale}
                    initiativeType={
                      selectedTemplate?.slug ?? selectedTemplate?.id ?? ""
                    }
                    cityId={basicInfo?.city_id ?? ""}
                    onValidSubmit={handleRewardsSubmit}
                    onJuthoorSubmit={handleJuthoorRewardsSubmit}
                    onBack={() => setStep(1)}
                    isSubmitting={submitting}
                  />
                )}
                {submitError && (
                  <p
                    className="mt-4 font-sans text-[10px] tracking-widest text-red-500 uppercase text-center"
                    role="alert"
                  >
                    {submitError}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="px-6 md:px-8 py-6 md:py-8 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
              {/* Left rail */}
              <aside className="lg:col-span-4">
                <StepHeader stepDef={currentStepDef} isRTL={isRTL} withTips />
              </aside>

              {/* Right body */}
              <div className="lg:col-span-8 min-w-0">
                {step === 0 && (
                  <WizardStepTemplate
                    locale={locale}
                    selectedTemplateId={selectedTemplate?.id || null}
                    onSelect={handleTemplateSelect}
                  />
                )}
                {step === 1 && (
                  <WizardStepBasicInfo
                    locale={locale}
                    initialData={
                      basicInfo || { template_id: selectedTemplate?.id || "" }
                    }
                    template={selectedTemplate}
                    onValidSubmit={handleBasicInfoSubmit}
                    onBack={() => setStep(0)}
                  />
                )}
                {submitError && (
                  <p
                    className="mt-4 font-sans text-[10px] tracking-widest text-red-500 uppercase text-center"
                    role="alert"
                  >
                    {submitError}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── Sticky footer (only step 0 controls; later steps own their own footers) ─ */}
        {step === 0 && (
          <div className="border-t border-border bg-card p-4 md:p-5 flex items-center justify-between gap-4">
            <span className="font-sans text-[10px] tracking-[0.25em] text-foreground-secondary uppercase">
              {currentStepDef.num} / 03
            </span>
            <button
              type="button"
              onClick={handleTemplateNext}
              disabled={!selectedTemplate}
              className={cn(
                "h-12 px-8 rounded-full inline-flex items-center justify-center gap-2",
                "bg-primary hover:bg-primary-dark text-white",
                "text-[14px] font-bold shadow-sm transition-all active:scale-[0.98]",
                "disabled:opacity-40 disabled:cursor-not-allowed",
              )}
            >
              {t(locale, "next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function StepHeader({
  stepDef,
  isRTL,
  withTips,
}: {
  stepDef: StepDef;
  isRTL: boolean;
  withTips?: boolean;
}) {
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h2 className="font-display font-bold text-2xl md:text-3xl text-foreground tracking-tight leading-tight">
          {isRTL ? stepDef.ar : stepDef.en}
        </h2>
        <p className="text-[14px] text-foreground-secondary leading-relaxed max-w-2xl">
          {isRTL ? stepDef.helperAr : stepDef.helperEn}
        </p>
      </div>
      {withTips && (
        <ul className="space-y-2 pt-3 border-t border-border">
          {(isRTL ? stepDef.tipsAr : stepDef.tipsEn).map((tip, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 font-sans text-[10px] tracking-[0.18em] uppercase text-foreground-secondary"
            >
              <span className="mt-1 w-1 h-1 rounded-full bg-primary shrink-0" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProgressRail({ step, isRTL }: { step: WizardStep; isRTL: boolean }) {
  return (
    <ol
      className="flex items-start gap-2"
      aria-label="Wizard steps"
    >
      {STEPS.map((s, idx) => {
        const state: StepDotState =
          idx < step ? "completed" : idx === step ? "current" : "pending";
        const isLast = idx === STEPS.length - 1;
        return (
          <li
            key={s.num}
            className={cn(
              "flex items-center gap-2 min-w-0",
              isLast ? "shrink-0" : "flex-1",
            )}
          >
            <div className="flex flex-col items-center gap-2 shrink-0">
              <StepDot label={s.num} state={state} />
              <span
                className={cn(
                  "font-sans text-[10px] tracking-[0.18em] uppercase whitespace-nowrap",
                  idx === step
                    ? "text-foreground"
                    : "text-foreground-secondary",
                )}
              >
                {isRTL ? s.ar : s.en}
              </span>
            </div>
            {!isLast && (
              <span
                className={cn(
                  "flex-1 h-px mt-4",
                  idx < step ? "bg-success" : "bg-border",
                )}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
